import { INSTAGRAM_AUTH_CODE_KEY } from '@blocs.auth';
import { useCallback } from 'react';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 800;
const AUTH_TIMEOUT = 120000;

const getPopupPosition = (): { top: number; left: number } => {
  const wLeft = window.screenLeft ?? window.screenX;
  const wTop = window.screenTop ?? window.screenY;

  return {
    left: wLeft + (window.innerWidth - POPUP_WIDTH) / 2,
    top: wTop + (window.innerHeight - POPUP_HEIGHT) / 2,
  };
};

const getPopupDimensions = (): string => {
  const { top, left } = getPopupPosition();
  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},top=${top},left=${left}`;
};

export const useInstagramAuth = () => {
  const onAuthCodeReceived = useCallback(async (popup: Window | null): Promise<string | null> => {
    if (!popup) {
      console.error('Popup window failed to open');
      return null;
    }

    const channel = new BroadcastChannel(INSTAGRAM_AUTH_CODE_KEY);

    const cleanup = (intervalId?: NodeJS.Timeout) => {
      channel.close();
      if (intervalId) clearInterval(intervalId);
    };

    try {
      return await new Promise((resolve, reject) => {
        // Check if popup is closed periodically
        const popupCheckInterval = setInterval(() => {
          if (popup == null) {
            cleanup(popupCheckInterval);
            reject(new Error('Authentication cancelled - popup closed'));
          }
        }, 1000);

        // Set timeout for auth
        const timeoutId = setTimeout(() => {
          cleanup(popupCheckInterval);
          reject(new Error('Instagram auth timed out after 2 minutes'));
        }, AUTH_TIMEOUT);

        channel.onmessage = (event: MessageEvent) => {
          // Validate message origin
          if (event.origin !== window.location.origin) {
            console.warn('Received message from unauthorized origin:', event.origin);
            return;
          }

          const { type, code, error } = event.data;

          // Clear timers and close channel before handling response
          cleanup(popupCheckInterval);
          clearTimeout(timeoutId);

          // Handle error responses
          if (type === 'error') {
            reject(new Error(`Instagram auth failed: ${error}`));
            return;
          }

          if (type !== 'success') {
            reject(new Error('Invalid response type received'));
            return;
          }

          // Validate auth code
          if (!code || typeof code !== 'string') {
            reject(new Error('Invalid or missing auth code'));
            return;
          }

          resolve(code);
        };
      });
    } catch (error) {
      cleanup();
      throw error;
    }
  }, []);

  return {
    getPopupDimensions,
    onAuthCodeReceived,
  };
};
